import React, { useState, useMemo, useRef } from 'react';
import { View, TouchableOpacity, StyleSheet, Animated } from 'react-native';
import { Text } from '@/components/common/text';
import { PADDING, CARD_STYLES } from '@/constants/residents/homeConstants';
import { useThemedStyles } from '@/hooks/shared/useThemeColors';
import type { ThemeContextValue } from '@/hooks/shared/useThemeColors';
import { formatRelativeDate } from '@/utils/residents/utils';
import { sanitizeResidentFiles } from '@/utils/shared/bulletins/sanitizeResidentFiles';
import { Bulletin } from '@/types/residents/bulletin';
import BulletinHeaderImage from '@/components/residents/bulletins/BulletinHeaderImage';

interface BulletinListItemProps {
  bulletin: Bulletin;
  onPress: (bulletin: Bulletin) => void;
}

export const BulletinListItem: React.FC<BulletinListItemProps> = ({ bulletin, onPress }) => {
  // iOS-style cell selection animation
  const scaleValue = useRef(new Animated.Value(1)).current;
  const styles = useThemedStyles(makeStyles);

  // Filter to only image files (matching iOS and ImageCarousel logic)
  const imageFiles = useMemo(() => sanitizeResidentFiles(bulletin.files), [bulletin.files]);
  
  const hasImage = imageFiles.length > 0;
  const isHighlighted = bulletin.highlight || false;

  // iOS-style selection animation handlers
  const handlePressIn = () => {
    Animated.timing(scaleValue, {
      toValue: 0.95, // iOS .Animation.selectionScale
      duration: 150, // Fast animation
      useNativeDriver: true,
    }).start();
  };

  const handlePressOut = () => {
    Animated.timing(scaleValue, {
      toValue: 1,
      duration: 150,
      useNativeDriver: true,
    }).start();
  };
  
  // Use the actual API field 'description' (iOS maps this to responseDescription)
  const descriptionText = bulletin.description || bulletin.responseDescription || '';
  
  // Remove verbose logging for production



  // Get card style with highlight border if needed
  const cardStyle = [
    styles.card,
    ...(isHighlighted ? [styles.highlightedCard] : [])
  ];

  return (
    <Animated.View style={{ transform: [{ scale: scaleValue }] }}>
      <TouchableOpacity
        style={cardStyle}
        onPress={() => onPress(bulletin)}
        onPressIn={handlePressIn}
        onPressOut={handlePressOut}
        accessibilityRole="button"
        accessibilityLabel={`Bulletin: ${bulletin.title}`}
        activeOpacity={1} // Disable TouchableOpacity's default opacity change
      >
      {/* Header Image - Optional */}
      {hasImage && (
        <View style={styles.imageContainer}>
          <BulletinHeaderImage uri={imageFiles[0].fileUrl || ''} />
        </View>
      )}
      
      {/* Content Stack */}
      <View style={styles.contentStack}>
                  {/* Date */}
          <Text style={styles.dateLabel}>
            {formatRelativeDate(bulletin.updatedAt)}
          </Text>
        
        {/* Title */}
        <Text style={styles.titleLabel} numberOfLines={2}>
          {bulletin.title}
        </Text>
        
        {/* Description - iOS: 3 lines, .Notifications.description font */}
        {descriptionText && (
          <Text style={styles.descriptionLabel} numberOfLines={3}>
            {descriptionText}
          </Text>
        )}
      </View>
    </TouchableOpacity>
    </Animated.View>
  );
};

const makeStyles = ({ colors, semantic }: ThemeContextValue) => StyleSheet.create({
  card: {
    ...CARD_STYLES, // iOS CardView styling
    backgroundColor: colors.card.bg, // theme-aware card surface
    marginHorizontal: PADDING.padding4,
    marginBottom: PADDING.padding4,
    overflow: 'hidden',
  },
  highlightedCard: {
    // iOS gradient border effect - simplified for React Native
    borderWidth: 2,
    borderColor: semantic.border.primary,
  },
  imageContainer: {
    position: 'relative',
    width: '100%',
    aspectRatio: 5/3, // iOS: heightAnchor.constraint(equalTo: cardView.widthAnchor, multiplier: 0.6) = 60% height
  },
  headerImage: {
    width: '100%',
    height: '100%', // Fill the container with correct aspect ratio
    borderTopLeftRadius: 14, // iOS: .CornerRadius.card = 14.0
    borderTopRightRadius: 14,
    backgroundColor: semantic.background.tertiary, // Placeholder background while loading
  },
  imageLoadingOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(255, 255, 255, 0.8)',
    justifyContent: 'center',
    alignItems: 'center',
    borderTopLeftRadius: 14,
    borderTopRightRadius: 14,
  },
  imageErrorOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(240, 240, 240, 0.9)',
    justifyContent: 'center',
    alignItems: 'center',
    borderTopLeftRadius: 14,
    borderTopRightRadius: 14,
  },
  imageErrorText: {
    fontSize: 12,
    color: colors.text.muted,
    textAlign: 'center',
    marginBottom: 8,
  },
  retryText: {
    fontSize: 10,
    color: colors.text.muted,
    textAlign: 'center',
    marginTop: 4,
  },
  retryButton: {
    backgroundColor: semantic.interactive.primary,
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 4,
    marginTop: 4,
  },
  retryButtonText: {
    fontSize: 11,
    color: 'white',
    textAlign: 'center',
    fontWeight: '500',
  },
  contentStack: {
    padding: 16, // iOS: .Padding.padding4 = sizingFactor * 4 = 16px (exact match)
    gap: 4, // iOS mainStackView.spacing = .Padding.padding1 = sizingFactor = 4px (exact match)
  },
  dateLabel: {
    fontSize: 13, // iOS .Notifications.tertiary: EuclidCircularB-Light, 13.0
    fontWeight: '300', // iOS Light weight
    color: colors.text.muted, // iOS .Text.date: gray600 #5E5E5E (exact match)
    lineHeight: 16,
  },
  titleLabel: {
    fontSize: 20, // iOS .Notifications.title: EuclidCircularB-SemiBold, 20.0
    fontWeight: '600', // iOS SemiBold weight
    color: colors.text.primary, // iOS .Text.primary: resioBlack #171717 (exact match)
    lineHeight: 24,
  },
  descriptionLabel: {
    fontSize: 15, // iOS .Notifications.description: EuclidCircularB-Regular, 15.0
    fontWeight: '400', // iOS Regular weight
    color: colors.text.primary, // iOS .Text.primary: resioBlack #171717 (exact match)
    lineHeight: 19, // Proper line height for 15pt font
    // backgroundColor: 'rgba(255, 0, 0, 0.1)', // DEBUG: Uncomment to see description area (for debugging layout)
  },
});